import type { ZatoonaCategoryMeta } from '../types'
import { zatoonaImage } from '@/shared/lib/media'

/**
 * The six Zatoona categories. Slugs match the API/DB exactly so the
 * client merges API rows with this fallback metadata cleanly. Counts
 * are placeholders; the API computes real counts when wired.
 */
export const ZATOONA_CATEGORIES: ZatoonaCategoryMeta[] = [
  {
    slug: 'camera-angles',
    label: { ar: 'زوايا الكاميرا', en: 'Camera Angles' },
    description: {
      ar: 'كل زاوية كاميرا واستخدامها مع برومبت جاهز.',
      en: 'Every camera angle, its use, and a ready-to-copy prompt.',
    },
    iconKey: 'camera',
    cover: '/zatoona-camera-angles.webp',
    count: 23,
    highlights: [
      { ar: 'زاوية منخفضة', en: 'Low angle' },
      { ar: 'Fish Eye', en: 'Fish Eye' },
      { ar: 'POV', en: 'POV' },
      { ar: 'Extreme Close-Up', en: 'Extreme Close-Up' },
    ],
    canonicalImage: zatoonaImage('camera-angles', '_canonical.webp'),
    canonicalCaption: {
      ar: 'نفس الشخص، نفس الإضاءة، 23 زاوية كاميرا مختلفة. الفرق في موقع الكاميرا والـ framing بس.',
      en: 'Same subject, same lighting, 23 distinct camera angles. The only thing that changes is the camera position and framing.',
    },
  },
  {
    slug: 'lighting-styles',
    label: { ar: 'أساليب الإضاءة', en: 'Lighting Styles' },
    description: {
      ar: 'أساليب الإضاءة من Rembrandt للإضاءة المنخفضة وحتى الـ neon.',
      en: 'Lighting setups from Rembrandt to low-key to neon.',
    },
    iconKey: 'lightbulb',
    cover: '/zatoona-lighting.webp',
    count: 10,
    highlights: [
      { ar: 'Rembrandt', en: 'Rembrandt' },
      { ar: 'Low-key', en: 'Low-key' },
      { ar: 'Backlight', en: 'Backlight' },
      { ar: 'Neon', en: 'Neon' },
    ],
  },
  {
    slug: 'shot-sizes',
    label: { ar: 'أحجام اللقطات', en: 'Shot Sizes' },
    description: {
      ar: 'من Extreme Long Shot للـ Extreme Close-Up — كل حجم لقطة وإمتى تستخدمها.',
      en: 'From Extreme Long Shot to Extreme Close-Up — every shot size and when to use it.',
    },
    iconKey: 'expand',
    cover: '/zatoona-pose-expression.webp',
    count: 9,
    highlights: [
      { ar: 'ELS', en: 'Extreme Long' },
      { ar: 'Wide', en: 'Wide' },
      { ar: 'Medium', en: 'Medium' },
      { ar: 'Close-Up', en: 'Close-Up' },
    ],
  },
  {
    slug: 'color-grading',
    label: { ar: 'التدرّج اللوني', en: 'Color Grading' },
    description: {
      ar: 'بصمات لونية سينمائية من Teal-Orange لـ Bleach Bypass.',
      en: 'Cinematic color grades from Teal-Orange to Bleach Bypass.',
    },
    iconKey: 'palette',
    cover: '/zatoona-color-grading.webp',
    count: 8,
    highlights: [
      { ar: 'Teal & Orange', en: 'Teal & Orange' },
      { ar: 'Bleach Bypass', en: 'Bleach Bypass' },
      { ar: 'Monochrome', en: 'Monochrome' },
      { ar: 'Pastel', en: 'Pastel' },
    ],
  },
  {
    slug: 'image-styles',
    label: { ar: 'ستايلات الصور', en: 'Image Styles' },
    description: {
      ar: 'من photorealistic لـ anime لـ isometric — بيوتقاطعها مع برومبتاتك.',
      en: 'Photorealistic to anime to isometric — paired with prompt fragments.',
    },
    iconKey: 'image',
    cover: '/zatoona-image-styles.webp',
    count: 70,
    highlights: [
      { ar: 'Photorealistic', en: 'Photorealistic' },
      { ar: 'Anime', en: 'Anime' },
      { ar: 'Oil paint', en: 'Oil paint' },
      { ar: 'Isometric', en: 'Isometric' },
    ],
  },
  {
    slug: 'camera-movements',
    label: { ar: 'حركة الكاميرا', en: 'Camera Movements' },
    description: {
      ar: 'Dolly، Push، Crane، Handheld — الحركات الأساسية للفيديو AI.',
      en: 'Dolly, push, crane, handheld — core moves for AI video.',
    },
    iconKey: 'video',
    cover: '/zatoona-camera-movements.webp',
    count: 10,
    highlights: [
      { ar: 'Dolly in', en: 'Dolly in' },
      { ar: 'Push', en: 'Push' },
      { ar: 'Crane', en: 'Crane' },
      { ar: 'Handheld', en: 'Handheld' },
    ],
  },
]

/**
 * Total references across all six categories. The home page reads
 * this as the count badge on the Zatoona tile (e.g. "+170 reference").
 */
export const ZATOONA_TOTAL = ZATOONA_CATEGORIES.reduce(
  (sum, c) => sum + c.count,
  0,
)
